// src/pages/admin/AISettings.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import AdminSidebar from '../../components/admin/AdminSidebar';
import { getAISettings, updateAISettings } from '../../utils/api';
import { 
  FaRobot, 
  FaSave, 
  FaSpinner, 
  FaInfoCircle, 
  FaHistory,
  FaUndo,
  FaShieldAlt,
  FaLightbulb,
  FaBrain
} from 'react-icons/fa';
import toast from 'react-hot-toast';

const AISettings = () => {
  const { user } = useAuth();
  const [instructions, setInstructions] = useState('');
  const [updatedAt, setUpdatedAt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [wordCount, setWordCount] = useState(0);
  const [showPreview, setShowPreview] = useState(false);

  const defaultTemplate = `You are BrasoBot, the AI assistant for Brasotech Investments Limited. Your role is to:

1. Answer questions about Brasotech's services, certifications, and operations
2. Provide information about their engineering capabilities in Zambia
3. Explain their compliance and registration status
4. Guide users to appropriate contact methods
5. Maintain a professional and helpful tone

COMPANY INFORMATION:
- Name: Brasotech Investments Limited
- Location: Zambia
- Services: Electrical Engineering, Mechanical Engineering, Civil Engineering, Plumbing, General Building Contracting
- Certifications: Registered with PACRA, ZRA, ZPPA, NCC, WCFCB, NAPSA
- Core Values: Honesty & Integrity, Customer Service, Quality, Teamwork

IMPORTANT GUIDELINES:
- Always respond professionally in English
- Be concise but thorough
- Don't speculate about pricing or specific project details
- Direct complex inquiries to the contact form
- Emphasize Zambian regulatory compliance
- If unsure, suggest contacting the company directly
- Use only the information provided in these instructions

If asked about topics not related to Brasotech, politely decline and redirect to Brasotech-related topics.`;

  useEffect(() => {
    loadAISettings();
  }, []);

  useEffect(() => {
    // Count words
    const words = instructions.trim().split(/\s+/);
    setWordCount(words.length > 1 ? words.length : 0);
  }, [instructions]);

  const loadAISettings = async () => {
    setIsLoading(true);
    try {
      const data = await getAISettings();
      setInstructions(data.instructions);
      setUpdatedAt(new Date(data.updated_at).toLocaleString());
    } catch (error) {
      console.error('Error loading AI settings:', error);
      toast.error('Failed to load AI settings');
      setInstructions(defaultTemplate);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (instructions.trim().length < 50) {
      toast.error('Instructions should be at least 50 characters');
      return;
    }

    setIsSaving(true);
    try {
      await updateAISettings(instructions);
      toast.success('AI instructions updated successfully!');
      
      // Reload to get updated timestamp
      const data = await getAISettings();
      setUpdatedAt(new Date(data.updated_at).toLocaleString());
    } catch (error) {
      console.error('Error saving AI settings:', error);
      toast.error(error.message || 'Failed to update instructions');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    if (window.confirm('Reset to default template? This will replace your current instructions.')) {
      setInstructions(defaultTemplate);
      toast.success('Loaded default template');
    }
  };

  const handlePreview = () => {
    setShowPreview(!showPreview);
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen bg-gray-100">
        <AdminSidebar />
        <div className="flex-1 p-8 flex items-center justify-center">
          <div className="text-center">
            <FaSpinner className="animate-spin h-8 w-8 text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">Loading AI settings...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-100">
      <AdminSidebar />
      
      <div className="flex-1 p-4 md:p-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg">
              <FaRobot className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800">AI Chatbot Settings</h1>
              <p className="text-gray-600">Manage what BrasoBot knows and how it responds</p>
            </div>
          </div>
          <div className="mt-2 md:mt-0 text-sm text-gray-500">
            <FaHistory className="inline mr-1" />
            Last updated: {updatedAt || 'Never'}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-lg">
                <FaLightbulb className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Word Count</p>
                <p className="text-lg font-bold">{wordCount}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-lg">
                <span className="text-lg font-bold">📝</span>
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Characters</p>
                <p className="text-lg font-bold">{instructions.length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="p-3 bg-purple-100 rounded-lg">
                <FaBrain className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">AI Status</p>
                <p className="text-lg font-bold text-green-600">Active</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="p-3 bg-orange-100 rounded-lg">
                <FaShieldAlt className="h-6 w-6 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Version</p>
                <p className="text-lg font-bold">v1.0</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex flex-col md:flex-row md:items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-800">AI Knowledge Base</h2>
                <p className="text-gray-600">Edit what the chatbot knows about Brasotech</p>
              </div>
              <div className="mt-2 md:mt-0 flex space-x-2">
                <button
                  onClick={handlePreview}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  {showPreview ? 'Hide Preview' : 'Show Preview'}
                </button>
                <button
                  onClick={handleReset}
                  className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors flex items-center"
                >
                  <FaUndo className="h-4 w-4 mr-2" />
                  Reset to Default
                </button>
              </div>
            </div>
          </div>

          <div className="p-6">
            {/* Editor and Preview Side by Side */}
            <div className={`grid ${showPreview ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'} gap-6 mb-6`}>
              {/* Editor */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Edit Instructions
                </label>
                <textarea
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                  rows={20}
                  className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none font-mono text-sm"
                  placeholder="Enter everything you want the AI to know about Brasotech..."
                />
                <div className="flex justify-between items-center mt-2">
                  <p className="text-sm text-gray-500">
                    {wordCount} words • {instructions.length} characters
                  </p>
                  <p className="text-sm text-gray-500">
                    Minimum 50 characters required
                  </p>
                </div>
              </div>

              {/* Preview */}
              {showPreview && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preview
                  </label>
                  <div className="h-full border border-gray-300 rounded-lg p-4 bg-gray-50 overflow-y-auto max-h-[500px]">
                    <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono">
                      {instructions.substring(0, 1000)}
                      {instructions.length > 1000 && '...'}
                    </pre>
                  </div>
                </div>
              )}
            </div>

            {/* Tips Section */}
            <div className="mb-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <FaInfoCircle className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-blue-800 mb-2">💡 Tips for Effective AI Instructions</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <ul className="text-sm text-blue-700 space-y-1">
                        <li className="flex items-start">
                          <span className="mr-2">•</span>
                          <span>Include all company information</span>
                        </li>
                        <li className="flex items-start">
                          <span className="mr-2">•</span>
                          <span>List services with clear descriptions</span>
                        </li>
                        <li className="flex items-start">
                          <span className="mr-2">•</span>
                          <span>Mention certifications and compliance</span>
                        </li>
                      </ul>
                      <ul className="text-sm text-blue-700 space-y-1">
                        <li className="flex items-start">
                          <span className="mr-2">•</span>
                          <span>Add response guidelines and tone</span>
                        </li>
                        <li className="flex items-start">
                          <span className="mr-2">•</span>
                          <span>Update when new services are added</span>
                        </li>
                        <li className="flex items-start">
                          <span className="mr-2">•</span>
                          <span>Test changes in the chatbot</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="text-sm text-gray-600">
                <FaInfoCircle className="inline mr-2" />
                Changes take effect immediately in the chatbot
              </div>
              <button
                onClick={handleSave}
                disabled={isSaving || instructions.trim().length < 50}
                className="flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow hover:shadow-md"
              >
                {isSaving ? (
                  <>
                    <FaSpinner className="animate-spin h-5 w-5 mr-2" />
                    Saving Changes...
                  </>
                ) : (
                  <>
                    <FaSave className="h-5 w-5 mr-2" />
                    Save AI Instructions
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Quick Guide Section */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center">
              <FaLightbulb className="h-5 w-5 text-blue-600 mr-2" />
              Update When
            </h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start">
                <span className="mr-2">✅</span>
                <span>New services are launched</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">✅</span>
                <span>Projects are completed</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">✅</span>
                <span>Certifications are renewed</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">✅</span>
                <span>Contact information changes</span>
              </li>
            </ul>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center">
              <FaShieldAlt className="h-5 w-5 text-green-600 mr-2" />
              What to Include
            </h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start">
                <span className="mr-2">📋</span>
                <span>Company details and location</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">⚙️</span>
                <span>Service descriptions</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">📄</span>
                <span>Certification information</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">📞</span>
                <span>Contact methods</span>
              </li>
            </ul>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center">
              <FaBrain className="h-5 w-5 text-purple-600 mr-2" />
              Testing Tips
            </h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start">
                <span className="mr-2">🧪</span>
                <span>Test with common questions</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">🎯</span>
                <span>Check service information accuracy</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">💬</span>
                <span>Verify response tone</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">✅</span>
                <span>Confirm contact info is correct</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AISettings;